import { createActionCreators, createReducerFunction, ImmerReducer } from 'immer-reducer';
import { LOCAL_ISO_CURRENCY_CODE } from '../../helpers/localized-description-helper';
import { IElectronicJournalRecord } from '../../interfaces/states/IElectronicJournalRecord';
import { IItemListState } from '../../interfaces/states/IItemListState';
import { EJRecordTypes } from '../../enumerations/ej-record-types';
import {
  CartItemViewData,
  CartPaymentData,
  CartTotalsViewData,
} from 'src/items-list/business-logic/service/interfaces/cart-response';
import { TaxRates } from 'src/action-window/business-logic/tax-rate.interface';

export const initialState: IItemListState = {
  ejItems: [],
  ejItemsFee: [],
  effectiveTaxRates: [],
  // BM - Eventually it might be worth moving the total stuff into it's own state?
  subTotal: 0,
  tax: 0,
  total: 0,
  savings: 0,
  itemCount: 0,
  selectedItemSequenceNumber: 0,
  amountDue: 0,
  currencyCode: LOCAL_ISO_CURRENCY_CODE,
  transactionSaved: false,
  transactionVoided: false,
  items: [],
  returns: [],
  payments: [],
};

class ItemListReducer extends ImmerReducer<IItemListState> {
  public updateItems(items: CartItemViewData[]) {
    this.draftState.items = [...(this.draftState.items ?? []), ...items];
  }

  public updatePayments(payments: CartPaymentData[]) {
    this.draftState.payments = [...(this.draftState.payments ?? []), ...payments];
  }

  public updateReturns(returns: CartItemViewData[]) {
    this.draftState.returns = [...(this.draftState.returns ?? []), ...returns];
  }

  public updateEJ(lineItems: IElectronicJournalRecord[], selectedLineId?: number): void {
    lineItems.forEach(updateItem => {
      if (updateItem.ItemLink) {
        const index = this.draftState.ejItems.findIndex(
          ejItem => ejItem.SequenceNumber === updateItem.ItemLink,
        );
        if (index >= 0) {
          if (updateItem.Voided) {
            if (updateItem.IsEndOfTicketPromotion) {
              this.draftState.ejItems.splice(index, 1);
            } else {
              this.draftState.ejItems[index].Voided = true;
            }
          }
          if (updateItem.Update) {
            if (updateItem.Type === EJRecordTypes.Coupon) {
              this.draftState.ejItems.splice(index, 1, updateItem);
            } else if (updateItem.Type === EJRecordTypes.Promotion) {
              this.draftState.ejItems.splice(index, 1);
              this.draftState.ejItems.push(updateItem);
            } else {
              updateItem.OriginalQuantity = this.draftState.ejItems[index].OriginalQuantity;
              updateItem.Type = this.draftState.ejItems[index].Type;
              this.draftState.ejItems[index] = updateItem;
            }
          }
        } else if (updateItem.Update) {
          this.draftState.ejItems.push(updateItem);
        }
      } else {
        this.draftState.ejItems.push(updateItem);
      }
    });

    this.adjustSelectedItems(selectedLineId);
    this.setTotalQuantity();
  }

  public setItemFeeList(newItemFees: IElectronicJournalRecord[]): void {
    this.draftState.ejItemsFee = newItemFees;
  }

  public setEffectiveTaxRates(rates: TaxRates[]): void {
    this.draftState.effectiveTaxRates = rates;
  }

  public setAmountDue(amount: number) {
    this.draftState.amountDue = amount;
  }

  public setTotalQuantity(): void {
    const totalQuantitiesById = new Map<string, number>();

    this.draftState.ejItems.forEach(({ ItemId, Quantity, Voided, Type, UnitOfMeasureCode }) => {
      const idWithType = ItemId?.concat(Type);
      if (idWithType && Quantity) {
        const units = UnitOfMeasureCode === 'EA' ? Quantity : 1;
        totalQuantitiesById.set(
          idWithType,
          (totalQuantitiesById.get(idWithType) || 0) + (Voided ? 0 : units),
        );
      }
    });
    this.draftState.ejItems.forEach(item => {
      const idWithType = item.ItemId?.concat(item.Type);
      item.TotalQuantity = totalQuantitiesById.get(idWithType ?? '');
    });
  }

  public setTransactionSaved(saved: boolean): void {
    this.draftState.transactionSaved = saved;
  }

  public setSequenceNumber(sequenceNumber: number): void {
    this.draftState.selectedItemSequenceNumber = sequenceNumber;
  }

  public setTransactionVoided(voided: boolean): void {
    this.draftState.transactionVoided = voided;
  }

  public removeAllRecords(): void {
    const taxRatesCopy = this.draftState.effectiveTaxRates;
    this.draftState = initialState;
    this.draftState.effectiveTaxRates = taxRatesCopy;
  }

  public setAllRecords(newState: IItemListState): void {
    this.draftState = newState;
  }

  public setTotals(data: Partial<CartTotalsViewData>, currency: string): void {
    this.draftState.subTotal = data?.netAmount || 0;
    this.draftState.tax = data?.taxAmounts?.taxAmount || 0;
    this.draftState.total = data?.balanceDue || 0;
    this.draftState.amountDue = data?.balanceDue || 0;
    this.draftState.itemCount = data?.itemsCount || 0;
    this.draftState.savings = data?.discountAmount || 0;
    this.draftState.currencyCode = currency;
  }

  public selectItem(
    sequenceNumber: number,
    eJRecordTypes?: EJRecordTypes,
    promotionId?: string,
  ): void {
    this.clearLastSelectedItem();
    let newSelectedLine: any = undefined;
    this.draftState.selectedItemSequenceNumber = sequenceNumber;
    if (sequenceNumber === -1) {
      return;
    }
    if (promotionId) {
      newSelectedLine = this.draftState.ejItems.find(
        (item): boolean => item.PromotionId === promotionId && item.Type === eJRecordTypes,
      );
    } else {
      newSelectedLine = this.draftState.ejItems.find(
        (item): boolean => item.SequenceNumber === sequenceNumber && item.Type === eJRecordTypes,
      );
    }
    if (newSelectedLine) {
      newSelectedLine.Selected = true;
    }
  }

  public setPayments(payments: CartPaymentData[]): void {
    this.draftState.payments = [...payments];
  }

  private adjustSelectedItems(selectedLineId?: number): void {
    if (this.draftState.ejItems.length > 0) {
      this.clearLastSelectedItem();

      if (selectedLineId) {
        const selectedElemt = this.draftState.ejItems.find(
          item => item.SequenceNumber === selectedLineId,
        );
        if (selectedElemt) {
          selectedElemt.Selected = true;
          this.draftState.selectedItemSequenceNumber = selectedElemt.SequenceNumber;
        }
      } else {
        const lastElement = this.draftState.ejItems[this.draftState.ejItems.length - 1];
        this.draftState.selectedItemSequenceNumber = lastElement.SequenceNumber;
        lastElement.Selected = true;
      }
    }
  }

  private clearLastSelectedItem(): void {
    const previousSelectedLine: IElectronicJournalRecord | undefined = this.draftState.ejItems.find(
      (x): boolean => x.Selected === true,
    );
    if (previousSelectedLine) {
      previousSelectedLine.Selected = false;
    }
  }
}

export const ItemListActions = createActionCreators(ItemListReducer);
export const ItemListReducerFunction = createReducerFunction(ItemListReducer, initialState);
